// CalibrationLogsTable.js
// Responsive (card + table) views for the standalone Calibration Log Book list.
// Reuses the sampling events table styles for visual consistency.

import styles from "../sampling_events/SamplingEventsTable.module.css";

const Empty = ({ message }) => (
  <div className={styles.emptyContainer}>
    <p>{message}</p>
  </div>
);

const Loading = () => (
  <div className={styles.loadingContainer}>
    <div className={styles.loadingSpinner}></div>
    <p>Loading calibration log books...</p>
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div className={styles.errorContainer}>
    <p className={styles.errorMessage}>{error}</p>
    <button className={styles.retryButton} onClick={onRetry}>
      Retry
    </button>
  </div>
);

// Active Calibration Log Books Table
export const ActiveCalibrationLogBooksTable = ({
  books,
  onRowSelect,
  onDoubleClick,
  onDelete,
  canDelete = () => false,
  selectedId,
  loading,
  error,
  pagination,
  onPageChange,
}) => {
  if (loading) return <Loading />;
  if (error)
    return (
      <ErrorState error={error} onRetry={() => onPageChange(pagination.currentPage)} />
    );
  if (!books || books.length === 0)
    return <Empty message="No active calibration log books found." />;

  return (
    <div className={styles.delTableContainer}>
      {/* Card View (Mobile & Tablet) */}
      <div className={styles.cardContainer}>
        <div className={styles.cardList}>
          {books.map((book) => (
            <div
              key={`card-${book.id}`}
              onClick={() => onRowSelect(book)}
              onDoubleClick={() => onDoubleClick(book.id)}
              className={`${styles.card} ${
                book.id === selectedId ? styles.selectedCard : ""
              }`}
            >
              <div className={styles.cardContent}>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Book Name:</span>
                  <span className={styles.value}>{book.book_name || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Book Date:</span>
                  <span className={styles.value}>{book.book_date || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Project Number:</span>
                  <span className={styles.value}>{book.project_number || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Phase ID:</span>
                  <span className={styles.value}>{book.phase_id || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Task ID:</span>
                  <span className={styles.value}>{book.task_id || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Created By:</span>
                  <span className={styles.value}>{book.created_by_name || "-"}</span>
                </div>
              </div>
              {canDelete(book) && (
                <div className={styles.cardActions}>
                  <button
                    className={styles.deleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(book.id);
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Table View (Desktop) */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.eventNameCol}>Book Name</th>
            <th className={styles.maxWidthCol}>Book Date</th>
            <th className={styles.maxWidthCol}>Project Number</th>
            <th className={styles.maxWidthCol}>Phase ID</th>
            <th className={styles.maxWidthCol}>Task ID</th>
            <th className={styles.createdByCol}>Created By</th>
            <th className={styles.deleteColumn}>Delete</th>
          </tr>
        </thead>
        <tbody>
          {books.map((book) => (
            <tr
              key={`row-${book.id}`}
              onClick={() => onRowSelect(book)}
              onDoubleClick={() => onDoubleClick(book.id)}
              className={book.id === selectedId ? styles.selectedRow : ""}
            >
              <td title={book.book_name} className={styles.eventNameCol}>
                {book.book_name || "-"}
              </td>
              <td title={book.book_date}>{book.book_date || "-"}</td>
              <td title={book.project_number || "-"}>{book.project_number || "-"}</td>
              <td title={book.phase_id || "-"}>{book.phase_id || "-"}</td>
              <td title={book.task_id || "-"}>{book.task_id || "-"}</td>
              <td title={book.created_by_name || "-"} className={styles.createdByCol}>
                {book.created_by_name || "-"}
              </td>
              <td className={styles.deleteColumn}>
                {canDelete(book) && (
                  <button
                    className={styles.deleteButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(book.id);
                    }}
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Completed Calibration Log Books Table (no delete - completed books are read-only here)
export const CompletedCalibrationLogBooksTable = ({
  books,
  onRowSelect,
  onDoubleClick,
  selectedId,
  loading,
  error,
  pagination,
  onPageChange,
}) => {
  if (loading) return <Loading />;
  if (error)
    return (
      <ErrorState error={error} onRetry={() => onPageChange(pagination.currentPage)} />
    );
  if (!books || books.length === 0)
    return <Empty message="No completed calibration log books found." />;

  return (
    <div className={styles.delTableContainer}>
      {/* Card View (Mobile & Tablet) */}
      <div className={styles.cardContainer}>
        <div className={styles.cardList}>
          {books.map((book) => (
            <div
              key={`card-${book.id}`}
              onClick={() => onRowSelect(book)}
              onDoubleClick={() => onDoubleClick(book.id)}
              className={`${styles.card} ${
                book.id === selectedId ? styles.selectedCard : ""
              }`}
            >
              <div className={styles.cardContent}>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Completed Date:</span>
                  <span className={styles.value}>{book.completed_date || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Completed By:</span>
                  <span className={styles.value}>{book.completed_by_name || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Book Name:</span>
                  <span className={styles.value}>{book.book_name || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Project Number:</span>
                  <span className={styles.value}>{book.project_number || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Phase ID:</span>
                  <span className={styles.value}>{book.phase_id || "-"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table View (Desktop) */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.maxWidthCol}>Completed Date</th>
            <th className={styles.maxWidthCol}>Completed Time</th>
            <th className={styles.createdByCol}>Completed By</th>
            <th className={styles.eventNameCol}>Book Name</th>
            <th className={styles.maxWidthCol}>Project Number</th>
            <th className={styles.maxWidthCol}>Phase ID</th>
            <th className={styles.maxWidthCol}>Task ID</th>
          </tr>
        </thead>
        <tbody>
          {books.map((book) => (
            <tr
              key={`row-${book.id}`}
              onClick={() => onRowSelect(book)}
              onDoubleClick={() => onDoubleClick(book.id)}
              className={book.id === selectedId ? styles.selectedRow : ""}
            >
              <td title={book.completed_date}>{book.completed_date || "-"}</td>
              <td title={book.completed_time}>{book.completed_time || "-"}</td>
              <td title={book.completed_by_name || "-"} className={styles.createdByCol}>
                {book.completed_by_name || "-"}
              </td>
              <td title={book.book_name} className={styles.eventNameCol}>
                {book.book_name || "-"}
              </td>
              <td title={book.project_number || "-"}>{book.project_number || "-"}</td>
              <td title={book.phase_id || "-"}>{book.phase_id || "-"}</td>
              <td title={book.task_id || "-"}>{book.task_id || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
